import React, { useEffect, useState } from 'react';
import axios from 'axios';
import WelcomeCard from '../../components/common/WelcomeCard';
import { FaEdit, FaTrash, FaUsers } from 'react-icons/fa';
import Loader from '../../components/common/Loader';
import toast from 'react-hot-toast';

// Add Client Modal
const AddClientModal = ({ open, handleClose, allClients }) => {
  const [companyName, setCompanyName] = useState('');
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(false);

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);

    const res = await axios.post(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/image/upload`,
      formData
    );
    return res.data.secure_url;
  };

  const handleLogoUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setLoading(true);
    try {
      const uploadPromises = Array.from(files).map((file) => uploadToCloudinary(file));
      const uploadedUrls = await Promise.all(uploadPromises);
      setLogos([...logos, ...uploadedUrls]);
      toast.success("Logos uploaded successfully");
    } catch (err) {
      toast.error("Logo upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyName.trim()) {
      toast.error("Please enter a company name");
      return;
    }
    if (logos.length === 0) {
      toast.error("Please upload at least one logo");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/our-client/create-client`, {
        companyName,
        logo: logos,
      });
      toast.success("Client added successfully");
      allClients();
      handleClose();
    } catch (error) {
      console.error("Error adding client:", error);
      toast.error("Failed to add client");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">Add New Client</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Company Name */}
          <div>
            <label className="block mb-2 font-medium">Company Name *</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full border rounded p-2"
              placeholder="Enter company name"
            />
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block mb-2 font-medium">Upload Logo *</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleLogoUpload}
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {logos.map((img, i) => (
                <div key={i} className="relative">
                  <img
                    src={img}
                    alt="logo"
                    className="w-20 h-20 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => setLogos(logos.filter((_, index) => index !== i))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="bg-gray-300 hover:bg-gray-400 px-6 py-2 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded disabled:opacity-50"
            >
              {loading ? <Loader /> : "Add Client"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Client Modal
const EditClientModal = ({ open, client, handleClose, allClients }) => {
  const [companyName, setCompanyName] = useState('');
  const [existingLogos, setExistingLogos] = useState([]);
  const [newLogos, setNewLogos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (client) {
      setCompanyName(client.companyName);
      setExistingLogos(client.logo || []);
    }
  }, [client]);

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", process.env.REACT_APP_UPLOAD_PRESET);

    const res = await axios.post(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/image/upload`,
      formData
    );
    return res.data.secure_url;
  };

  const handleLogoUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setLoading(true);
    try {
      const uploadPromises = Array.from(files).map((file) => uploadToCloudinary(file));
      const uploadedUrls = await Promise.all(uploadPromises);
      setNewLogos([...newLogos, ...uploadedUrls]);
      toast.success("New logos uploaded successfully");
    } catch (err) {
      toast.error("Logo upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalLogos = [...existingLogos, ...newLogos];

    if (!companyName.trim()) {
      toast.error("Company name is required");
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/our-client/update-client/${client._id}`, {
        companyName,
        logo: finalLogos,
      });
      toast.success("Client updated successfully");
      allClients();
      handleClose();
    } catch (error) {
      console.error("Error updating client:", error);
      toast.error("Failed to update client");
    } finally {
      setLoading(false);
    }
  };

  if (!open || !client) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">Edit Client</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Company Name */}
          <div>
            <label className="block mb-2 font-medium">Company Name *</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full border rounded p-2"
            />
          </div>

          {/* Existing Logos */}
          <div>
            <label className="block mb-2 font-medium">Existing Logos</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {existingLogos.map((img, i) => (
                <div key={i} className="relative">
                  <img
                    src={img}
                    alt="logo"
                    className="w-20 h-20 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => setExistingLogos(existingLogos.filter((_, index) => index !== i))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Upload New Logos */}
          <div>
            <label className="block mb-2 font-medium">Upload New Logos</label>
            <input type="file" accept="image/*" multiple onChange={handleLogoUpload} />
            <div className="flex flex-wrap gap-2 mt-2">
              {newLogos.map((img, i) => (
                <div key={i} className="relative">
                  <img
                    src={img}
                    alt="logo"
                    className="w-20 h-20 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => setNewLogos(newLogos.filter((_, index) => index !== i))}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="bg-gray-300 hover:bg-gray-400 px-6 py-2 rounded"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded disabled:opacity-50"
            >
              {loading ? <Loader /> : "Update Client"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Our Client Component
const OurClient = () => {
  const [clientList, setClientList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    allClients();
  }, []);

 const allClients = async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/our-client/all-clients`);
    const data = Array.isArray(res.data) ? res.data : res.data.clients || res.data.data || [];
    setClientList(data);
  } catch (error) {
    console.error("Error fetching clients:", error);
    toast.error("Failed to load clients");
  } finally {
    setLoading(false);
  }
};


  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/our-client/delete-client/${id}`);
        toast.success("Client deleted successfully");
        allClients();
      } catch (error) {
        console.error("Error deleting client:", error);
        toast.error("Failed to delete client");
      }
    }
  };

  return (
    <>
      <AddClientModal open={openAddModal} handleClose={() => setOpenAddModal(false)} allClients={allClients} />
      {selectedClient && (
        <EditClientModal
          open={openEditModal}
          client={selectedClient}
          handleClose={() => {
            setOpenEditModal(false);
            setSelectedClient(null);
          }}
          allClients={allClients}
        />
      )}

      <div className="p-4">
        <WelcomeCard />
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-semibold text-[#000000] tracking-wider">Our Clients</h1>
          <button
            onClick={() => setOpenAddModal(true)}
            className="flex justify-center items-center bg-gradient-to-r from-[#FF3131] to-[#ff9301] text-white font-semibold tracking-wider py-2 px-4 rounded-md hover:bg-red-600 transition duration-300 ease-in-out"
          >
            <FaUsers size={20} className="mr-2" /> Add New Client
          </button>
        </div>

        {loading ? (
          <div className='rounded-md flex tracking-wider font-semibold justify-center items-center border border-slate-400 w-full p-4'>
            <Loader /> <span className='ml-2'>Loading Clients...</span>
          </div>
        ) : clientList.length === 0 ? (
          <div className='rounded-md flex tracking-wider font-semibold justify-center items-center border border-slate-400 w-full p-4'>
            No clients found.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {clientList.map(client => (
              <div key={client._id} className="bg-white shadow-md rounded-lg overflow-hidden hover:scale-105 transition">
                {client.logo && client.logo.length > 0 && (
                  <img src={client.logo[0]} alt="logo" className="w-full h-40 object-contain p-4" />
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-800 text-lg">{client.companyName}</h3>
                  <div className="flex justify-end space-x-4 mt-3">
                    <button onClick={() => { setSelectedClient(client); setOpenEditModal(true); }} className="text-blue-500 hover:text-blue-700">
                      <FaEdit size={18} />
                    </button>
                    <button onClick={() => handleDelete(client._id)} className="text-red-500 hover:text-red-700">
                      <FaTrash size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default OurClient;
